// /src/pages/PlantDetail.jsx
import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, MapPin, Droplets, Sun, Thermometer, Calendar, Share2, BookOpen, Users, Heart } from 'lucide-react'
import api from '../utils/api'

const PlantDetail = () => {
  const { id } = useParams()
  const [plant, setPlant] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  // Helper function to get the correct image URL
  const getImageUrl = (imageField) => {
    if (!imageField) return '/placeholder-plant.jpg'
    
    // If it's already a full URL (external image), use it as is
    if (imageField.startsWith('http')) {
      return imageField
    }
    
    // If it's a filename from our uploads, construct the proxy URL
    if (imageField.includes('/')) {
      // It's already a path like "plants/1234567890-abc123.jpg"
      return `https://www.palmgardenrs.com/images/${imageField}`
    }
    
    // Fallback for other cases
    return imageField
  }

  // Helper function to get all images from plant data
  const getAllImages = (plant) => {
    if (!plant) return []
    
    // First try to get from images array (new format)
    if (plant.images && Array.isArray(plant.images) && plant.images.length > 0) {
      return plant.images.map(img => getImageUrl(img))
    }
    // Fall back to single image field (backward compatibility)
    if (plant.image) {
      return [getImageUrl(plant.image)]
    }
    return []
  }

  // Helper function to get the main image
  const getMainImage = (plant) => {
    const images = getAllImages(plant)
    return images.length > 0 ? images[selectedImageIndex] : '/placeholder-plant.jpg'
  }

  useEffect(() => {
    const fetchPlant = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Try to fetch from API first
        const plantData = await api.getPlant(id)
        setPlant(plantData)
      } catch (apiError) {
        console.error('Failed to fetch plant from API:', apiError)
        
        // Fallback to mock data if API fails
        const mockPlant = {
          id: parseInt(id),
          name: 'Monstera Deliciosa',
          scientificName: 'Monstera deliciosa',
          commonNames: ['Swiss Cheese Plant', 'Split-Leaf Philodendron'],
          family: 'Araceae',
          category: 'trees',
          image: 'https://images.unsplash.com/photo-1545249390-6bdfa286032f?w=800&h=600&fit=crop',
          habitat: 'Tropical rainforests of Central America',
          nativeRange: 'Mexico, Panama, Costa Rica',
          careLevel: 'Easy',
          description: 'A popular houseplant known for its distinctive leaves with natural holes and splits. This tropical plant is native to the rainforests of Central America and has become one of the most recognizable indoor plants worldwide.',
          longDescription: 'The Monstera deliciosa is a climbing evergreen perennial vine that can grow up to 20 meters in height. Its distinctive leaves develop holes and splits as they mature, giving it the nickname "Swiss Cheese Plant." This adaptation allows light to reach lower leaves in the dense rainforest canopy.',
          careInstructions: {
            light: 'Bright, indirect light. Can tolerate some direct morning sun.',
            water: 'Water when top 2-3 inches of soil is dry. Keep soil consistently moist but not soggy.',
            temperature: '65-85°F (18-29°C). Avoid temperatures below 60°F.',
            humidity: 'High humidity (60-80%). Mist leaves regularly or use a humidifier.',
            soil: 'Well-draining potting mix with peat moss, perlite, and orchid bark.',
            fertilizer: 'Feed monthly during growing season with balanced liquid fertilizer.'
          },
          propagation: 'Stem cuttings in water or soil, air layering',
          pests: ['Spider mites', 'Mealybugs', 'Scale insects'],
          diseases: ['Root rot', 'Leaf spot', 'Bacterial leaf blight'],
          toxicity: 'Toxic to pets and humans if ingested',
          growthRate: 'Moderate',
          maxHeight: '20 meters',
          flowering: 'Rarely flowers indoors',
          fruit: 'Edible fruit (only when fully ripe)',
          conservationStatus: 'Least Concern',
          relatedPlants: [
            { id: 2, name: 'Philodendron', image: 'https://images.unsplash.com/photo-1593691509543-c55fb32e5cee?w=200&h=200&fit=crop' },
            { id: 3, name: 'Pothos', image: 'https://images.unsplash.com/photo-1593691509543-c55fb32e5cee?w=200&h=200&fit=crop' },
            { id: 4, name: 'Anthurium', image: 'https://images.unsplash.com/photo-1593691509543-c55fb32e5cee?w=200&h=200&fit=crop' }
          ]
        }
        setPlant(mockPlant)
        setError('Using demo data - API not available')
      } finally {
        setLoading(false)
      }
    }

    fetchPlant()
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen bg-forest-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-forest-600">Loading plant details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-forest-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-yellow-100 text-yellow-800 p-4 rounded-lg mb-4">
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!plant) {
    return (
      <div className="min-h-screen bg-forest-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-forest-600">Plant not found</p>
          <Link to="/wiki" className="btn-primary mt-4">
            Back to Plants
          </Link>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BookOpen },
    { id: 'care', name: 'Care Guide', icon: Droplets },
    { id: 'habitat', name: 'Habitat', icon: MapPin },
    { id: 'related', name: 'Related', icon: Users }
  ]

  return (
    <div className="min-h-screen bg-forest-50">
      {/* Header */}
      <div className="bg-white border-b border-forest-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4 mb-6">
            <Link to="/wiki" className="btn-secondary inline-flex items-center space-x-2">
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Plants</span>
            </Link>
            <div className="flex-1"></div>
            <button className="btn-secondary inline-flex items-center space-x-2">
              <Heart className="w-4 h-4" />
              <span>Save</span>
            </button>
            <button className="btn-secondary inline-flex items-center space-x-2">
              <Share2 className="w-4 h-4" />
              <span>Share</span>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Plant Image Gallery */}
            <div>
              <div className="relative">
                <img
                  src={getMainImage(plant)}
                  alt={plant.name}
                  className="w-full h-96 object-cover rounded-xl"
                  onError={(e) => {
                    console.error('Image failed to load:', getMainImage(plant));
                    e.target.src = '/placeholder-plant.jpg';
                  }}
                />
                
                {/* Navigation arrows for multiple images */}
                {getAllImages(plant).length > 1 && (
                  <>
                    <button
                      onClick={() => setSelectedImageIndex(prev => 
                        prev === 0 ? getAllImages(plant).length - 1 : prev - 1
                      )}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity"
                    >
                      ←
                    </button>
                    <button
                      onClick={() => setSelectedImageIndex(prev => 
                        prev === getAllImages(plant).length - 1 ? 0 : prev + 1
                      )}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity"
                    >
                      →
                    </button>
                  </>
                )}
              </div>
              
              {/* Thumbnail gallery */}
              {getAllImages(plant).length > 1 && (
                <div className="flex space-x-2 mt-4 overflow-x-auto">
                  {getAllImages(plant).map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === index 
                          ? 'border-primary-500 scale-110' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${plant.name} - Image ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.error('Thumbnail failed to load:', image);
                          e.target.src = '/placeholder-plant.jpg';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Plant Info */}
            <div>
              <h1 className="text-4xl font-bold text-forest-900 mb-2">{plant.name}</h1>
              <p className="text-xl text-forest-600 italic mb-4">{plant.scientificName}</p>
              
              {plant.commonNames && plant.commonNames.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {plant.commonNames.map((name, index) => (
                    <span key={index} className="bg-forest-100 text-forest-700 px-3 py-1 rounded-full text-sm">
                      {name}
                    </span>
                  ))}
                </div>
              )}

              <p className="text-forest-700 mb-6">{plant.description}</p>

              {/* Quick Facts */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {plant.family && (
                  <div className="bg-forest-50 p-4 rounded-lg">
                    <div className="text-sm text-forest-600 mb-1">Family</div>
                    <div className="font-semibold text-forest-900">{plant.family}</div>
                  </div>
                )}
                {plant.careLevel && (
                  <div className="bg-forest-50 p-4 rounded-lg">
                    <div className="text-sm text-forest-600 mb-1">Care Level</div>
                    <div className="font-semibold text-primary-700">{plant.careLevel}</div>
                  </div>
                )}
                {plant.growthRate && (
                  <div className="bg-forest-50 p-4 rounded-lg">
                    <div className="text-sm text-forest-600 mb-1">Growth Rate</div>
                    <div className="font-semibold text-forest-900">{plant.growthRate}</div>
                  </div>
                )}
                {plant.maxHeight && (
                  <div className="bg-forest-50 p-4 rounded-lg">
                    <div className="text-sm text-forest-600 mb-1">Max Height</div>
                    <div className="font-semibold text-forest-900">{plant.maxHeight}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-forest-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-700'
                      : 'border-transparent text-forest-500 hover:text-forest-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-forest-900 mb-4">About {plant.name}</h2>
              <p className="text-forest-700 mb-6 leading-relaxed">{plant.longDescription || plant.description}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {plant.nativeRange && (
                  <div>
                    <h3 className="text-lg font-semibold text-forest-900 mb-3">Native Range</h3>
                    <p className="text-forest-700">{plant.nativeRange}</p>
                  </div>
                )}
                {plant.conservationStatus && (
                  <div>
                    <h3 className="text-lg font-semibold text-forest-900 mb-3">Conservation Status</h3>
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                      {plant.conservationStatus}
                    </span>
                  </div>
                )}
                {plant.propagation && (
                  <div>
                    <h3 className="text-lg font-semibold text-forest-900 mb-3">Propagation</h3>
                    <p className="text-forest-700">{plant.propagation}</p>
                  </div>
                )}
                {plant.toxicity && (
                  <div>
                    <h3 className="text-lg font-semibold text-forest-900 mb-3">Toxicity</h3>
                    <p className="text-forest-700">{plant.toxicity}</p>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <div className="card">
                <h3 className="text-lg font-semibold text-forest-900 mb-4">Quick Facts</h3>
                <div className="space-y-3">
                  {plant.flowering && (
                    <div className="flex justify-between">
                      <span className="text-forest-600">Flowering</span>
                      <span className="text-forest-900">{plant.flowering}</span>
                    </div>
                  )}
                  {plant.fruit && (
                    <div className="flex justify-between">
                      <span className="text-forest-600">Fruit</span>
                      <span className="text-forest-900">{plant.fruit}</span>
                    </div>
                  )}
                  {plant.growthRate && (
                    <div className="flex justify-between">
                      <span className="text-forest-600">Growth Rate</span>
                      <span className="text-forest-900">{plant.growthRate}</span>
                    </div>
                  )}
                  {plant.maxHeight && (
                    <div className="flex justify-between">
                      <span className="text-forest-600">Max Height</span>
                      <span className="text-forest-900">{plant.maxHeight}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'care' && plant.careInstructions && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="text-2xl font-bold text-forest-900 mb-6">Care Instructions</h2>
              <div className="space-y-6">
                {Object.entries(plant.careInstructions).map(([key, value]) => (
                  <div key={key} className="card">
                    <div className="flex items-center space-x-3 mb-3">
                      {key === 'light' && <Sun className="w-5 h-5 text-yellow-500" />}
                      {key === 'water' && <Droplets className="w-5 h-5 text-blue-500" />}
                      {key === 'temperature' && <Thermometer className="w-5 h-5 text-red-500" />}
                      {key === 'humidity' && <Droplets className="w-5 h-5 text-blue-500" />}
                      {key === 'soil' && <div className="w-5 h-5 bg-earth-500 rounded"></div>}
                      {key === 'fertilizer' && <div className="w-5 h-5 bg-green-500 rounded"></div>}
                      <h3 className="text-lg font-semibold text-forest-900 capitalize">{key}</h3>
                    </div>
                    <p className="text-forest-700">{value}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-forest-900 mb-6">Common Issues</h2>
              <div className="space-y-6">
                {plant.pests && plant.pests.length > 0 && (
                  <div className="card">
                    <h3 className="text-lg font-semibold text-forest-900 mb-3">Pests</h3>
                    <div className="flex flex-wrap gap-2">
                      {plant.pests.map((pest, index) => (
                        <span key={index} className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
                          {pest}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {plant.diseases && plant.diseases.length > 0 && (
                  <div className="card">
                    <h3 className="text-lg font-semibold text-forest-900 mb-3">Diseases</h3>
                    <div className="flex flex-wrap gap-2">
                      {plant.diseases.map((disease, index) => (
                        <span key={index} className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                          {disease}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'habitat' && (
          <div>
            <h2 className="text-2xl font-bold text-forest-900 mb-6">Natural Habitat</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                {plant.habitat && (
                  <>
                    <h3 className="text-lg font-semibold text-forest-900 mb-3">Habitat Description</h3>
                    <p className="text-forest-700 mb-6">{plant.habitat}</p>
                  </>
                )}
                
                {plant.nativeRange && (
                  <>
                    <h3 className="text-lg font-semibold text-forest-900 mb-3">Native Range</h3>
                    <p className="text-forest-700">{plant.nativeRange}</p>
                  </>
                )}
              </div>
              
              <div className="card">
                <h3 className="text-lg font-semibold text-forest-900 mb-4">Environmental Conditions</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-forest-600">Climate</span>
                    <span className="text-forest-900">Tropical</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-forest-600">Humidity</span>
                    <span className="text-forest-900">High (60-80%)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-forest-600">Temperature</span>
                    <span className="text-forest-900">65-85°F (18-29°C)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-forest-600">Rainfall</span>
                    <span className="text-forest-900">High</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'related' && plant.relatedPlants && plant.relatedPlants.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-forest-900 mb-6">Related Plants</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {plant.relatedPlants.map((relatedPlant) => (
                <Link key={relatedPlant.id} to={`/plant/${relatedPlant.id}`} className="card group">
                  <img
                    src={getImageUrl(relatedPlant.image)}
                    alt={relatedPlant.name}
                    className="w-full h-48 object-cover rounded-lg mb-4 group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      console.error('Related plant image failed to load:', relatedPlant.image);
                      e.target.src = '/placeholder-plant.jpg';
                    }}
                  />
                  <h3 className="text-lg font-semibold text-forest-900 group-hover:text-primary-600 transition-colors duration-200">
                    {relatedPlant.name}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlantDetail 